import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import {
	useGetGoogleBusinessConnection,
	usePutGoogleBusinessSyncPreferences,
	useSyncGoogleBusiness,
} from '../../../../queries';
import { formatLocationAddress } from '../../../../utils/googleBusinessUtils';
import { IModalProps, Modal } from '../../../../web/components/Modal';
import { baseStyleSheet } from '../../../../web/styles/styles';
import { styleSheet } from './styles';

export interface IGoogleBusinessSetupModalProps<TResult = any> extends IModalProps<TResult> {
	impersonationContext: Api.IImpersonationContext;
	isOpen: boolean;
	setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

export const GoogleBusinessSetupModal = ({
	impersonationContext,
	isOpen,
	setIsOpen,
	...restProps
}: IGoogleBusinessSetupModalProps) => {
	const [hasChanges, setHasChanges] = React.useState(false);
	const [connectionToEdit, setConnectionToEdit] = React.useState<Api.IGoogleBusinessConnection>(null);

	const connectionQuery = useGetGoogleBusinessConnection({
		enabled: isOpen,
		impersonationContext,
	});

	React.useEffect(() => {
		if (connectionQuery.data) {
			setConnectionToEdit(connectionQuery.data);
		}
	}, [connectionQuery.data]);

	const syncGoogleBusinessMutation = useSyncGoogleBusiness({
		impersonationContext,
		onSuccess: () => {
			setIsOpen(false);
			setHasChanges(false);
		},
	});

	const syncPreferencesMutation = usePutGoogleBusinessSyncPreferences({
		impersonationContext,
		onSuccess: () => syncGoogleBusinessMutation.mutate(),
	});

	const onSave = () => {
		syncPreferencesMutation.mutate(connectionToEdit);
	};

	const onCancel = () => {
		setIsOpen(false);
		setHasChanges(false);
	};

	const onEnableAccount = (account: Api.IGoogleBusinessAccount) => {
		const index = connectionToEdit.googleBusinessAccounts.findIndex(
			accountToEdit => accountToEdit.name === account.name
		);
		const updatedConnection = { ...connectionToEdit };
		updatedConnection.googleBusinessAccounts[index].sync = true;
		setConnectionToEdit(updatedConnection);
		setHasChanges(true);
	};
	const onEnableLocation = (location: Api.IGoogleBusinessLocation) => {
		const index = connectionToEdit.locations.findIndex(locationToEdit => locationToEdit.name === location.name);
		const updatedConnection = { ...connectionToEdit };
		updatedConnection.locations[index].sync = true;
		setConnectionToEdit(updatedConnection);
		setHasChanges(true);
	};
	const onDisableAccount = (account: Api.IGoogleBusinessAccount) => {
		const index = connectionToEdit.googleBusinessAccounts.findIndex(
			accountToEdit => accountToEdit.name === account.name
		);
		const updatedConnection = { ...connectionToEdit };
		updatedConnection.googleBusinessAccounts[index].sync = false;
		setConnectionToEdit(updatedConnection);
		setHasChanges(true);
	};
	const onDisableLocation = (location: Api.IGoogleBusinessLocation) => {
		const index = connectionToEdit.locations.findIndex(locationToEdit => locationToEdit.name === location.name);
		const updatedConnection = { ...connectionToEdit };
		updatedConnection.locations[index].sync = false;
		setConnectionToEdit(updatedConnection);
		setHasChanges(true);
	};

	if (!isOpen || connectionQuery.isPending) {
		return null;
	}

	const titleCase = (value: string) => {
		const newValue = value.toLowerCase();
		return `${newValue[0].toUpperCase()}${newValue.slice(1)}`;
	};

	const accounts = connectionQuery.data?.googleBusinessAccounts || [];
	const locations = connectionQuery.data?.locations || [];

	return (
		<Modal
			{...restProps}
			isOpen={isOpen}
			overlayPresentationStyle={{
				className: 'modal-overlay',
				transitionInDuration: 500,
				transitionOutDuration: 500,
			}}
			className={css(styleSheet.modal)}
			useDefaultHeader
		>
			<div className={css(styleSheet.container)}>
				<h2 className={css(styleSheet.title)}>Manage Google Business</h2>

				<div className={css(styleSheet.content)}>
					<div>
						<h4 className={css(styleSheet.subTitle)}>Accounts</h4>

						<div className={css(styleSheet.table)}>
							<div className={css(styleSheet.tableRow)}>
								<div className={css(styleSheet.th, styleSheet.nameCell)}>Name / Account</div>
								<div className={css(styleSheet.th, styleSheet.mediumCell)}>Type</div>
								<div className={css(styleSheet.th, styleSheet.lastTd, styleSheet.titleCell, styleSheet.noBorder)}>
									&nbsp;
								</div>
							</div>

							{accounts.map((account, index) => {
								return (
									<div className={css(styleSheet.tableRow)} key={account.name}>
										<div
											className={css(styleSheet.td, styleSheet.nameCell, index === 0 ? styleSheet.bottomLeftTd : null)}
										>
											<div className={css(styleSheet.nameCellContent)}>
												<span className={css(styleSheet.name)}>{account.accountName}</span>
												<span className={css(styleSheet.secondRow)}>{titleCase(account.name)}</span>
											</div>
										</div>
										<div
											className={css(
												styleSheet.td,
												styleSheet.lastTd,
												styleSheet.mediumCell,
												index + 1 === accounts.length ? styleSheet.bottomRightTd : null
											)}
										>
											{titleCase(account.type)}
										</div>
										<div className={css(styleSheet.actions)}>
											{account.sync === false ? (
												<button onClick={() => onEnableAccount(account)} className={css(baseStyleSheet.ctaButtonSmall)}>
													Re-enable Sync
												</button>
											) : (
												<button
													onClick={() => onDisableAccount(account)}
													className={css(baseStyleSheet.ctaButtonDestructiveSmall)}
												>
													Disable Sync
												</button>
											)}
										</div>
									</div>
								);
							})}
						</div>

						<h4 className={css(styleSheet.subTitle)}>Locations</h4>

						<div className={css(styleSheet.table)}>
							<div className={css(styleSheet.tableRow)}>
								<div className={css(styleSheet.th, styleSheet.nameCell)}>Name / Account</div>
								<div className={css(styleSheet.th, styleSheet.smallCell, styleSheet.centered)}>
									<span className={css(styleSheet.fullCell)}>Rating</span>
								</div>
								<div className={css(styleSheet.th, styleSheet.mediumCell, styleSheet.centered)}>
									<span className={css(styleSheet.fullCell)}>Review count</span>
								</div>
								<div className={css(styleSheet.th, styleSheet.titleCell)}>Title</div>
								<div className={css(styleSheet.th, styleSheet.mediumCell)}>Location</div>
								<div className={css(styleSheet.th, styleSheet.lastTd, styleSheet.mediumCell, styleSheet.noBorder)}>
									&nbsp;
								</div>
							</div>

							{locations.map((location, index) => {
								return (
									<div className={css(styleSheet.tableRow)} key={location.name}>
										<div
											className={css(styleSheet.td, styleSheet.nameCell, index === 0 ? styleSheet.bottomLeftTd : null)}
										>
											<div className={css(styleSheet.nameCellContent)}>
												<span className={css(styleSheet.name)}>{titleCase(location.name)}</span>
												<span className={css(styleSheet.secondRow)}>{titleCase(location.accountName)}</span>
											</div>
										</div>
										<div className={css(styleSheet.td, styleSheet.smallCell, styleSheet.centered)}>
											<span className={css(styleSheet.fullCell)}>{location.rating}</span>
										</div>
										<div className={css(styleSheet.td, styleSheet.mediumCell, styleSheet.centered)}>
											<span className={css(styleSheet.fullCell)}>{location.reviewCount}</span>
										</div>
										<div className={css(styleSheet.td, styleSheet.titleCell)}>{location.title}</div>
										<div
											className={css(
												styleSheet.td,
												styleSheet.lastTd,
												styleSheet.mediumCell,
												index + 1 === accounts.length ? styleSheet.bottomRightTd : null
											)}
										>
											{formatLocationAddress(location) || '—'}
										</div>
										<div className={css(styleSheet.actions)}>
											{location.sync === false ? (
												<button
													onClick={() => onEnableLocation(location)}
													className={css(baseStyleSheet.ctaButtonSmall)}
												>
													Re-enable Sync
												</button>
											) : (
												<button
													onClick={() => onDisableLocation(location)}
													className={css(baseStyleSheet.ctaButtonDestructiveSmall)}
												>
													Disable Sync
												</button>
											)}
										</div>
									</div>
								);
							})}
						</div>
					</div>
				</div>

				<footer className={css(styleSheet.footer)}>
					<button className={css(baseStyleSheet.ctaButton)} onClick={onSave} disabled={!hasChanges}>
						<span>Save</span>
					</button>
					<button className={css(baseStyleSheet.ctaButtonReverse)} onClick={onCancel}>
						<span>Cancel</span>
					</button>
				</footer>
			</div>
		</Modal>
	);
};
